import { Button, Container, Grid, Paper, Stack, Typography, Box } from '@mui/material';
import { FormProvider, SelectTag, TextField } from '../../../../../components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { BASE_URL, dropdownFetch, request, toast } from '../../../../../utils';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

export const OpdReferralReport = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [referralDropdown, setReferralDropdown] = useState([]);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        from_date: Yup.string().required(' '),
        to_date: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date().toISOString().split('T')[0],
      refby: 'all'
    },
    mode: 'all'
  });

  const { watch } = methods;

  const getReferralDropdown = useCallback(async () => {
    const data = await dropdownFetch('referral');
    setReferralDropdown(data);
  }, []);

  useEffect(() => {
    getReferralDropdown();
  }, [getReferralDropdown]);

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const buildRefId = () =>
    JSON.stringify({
      refby: watch('refby'),
      from_date: watch('from_date'),
      to_date: watch('to_date')
    });

  const handleExcelReport = async () => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    try {
      // Use the SAME endpoint AND SAME refId params as the PDF report
      const response = await axios.get(
        `${BASE_URL}/print/hospital/outpatient-management/opd-referral-report?refId=${buildRefId()}`
      );

      if (!response.data.success) {
        toast('Something Went Wrong', { variant: 'error' });
        return;
      }

      const d = response.data.data;
      const opdRows     = d?.opdData?.data        || [];
      const opdBillRows = d?.opdBillingData?.data || [];
      const referralSummary = d?.referralSummary  || [];

      const opdStats     = d?.opdData?.stats        || {};
      const opdBillStats = d?.opdBillingData?.stats || {};

      const totalRows = opdRows.length + opdBillRows.length;
      if (totalRows === 0) {
        toast('No Data Found For These Filters', { variant: 'error' });
        return;
      }

      // ── Helpers ──────────────────────────────────────────────────
      const n = (v) => Number(v) || 0;
      const rupees = (v) => n(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const patName = (...parts) => parts.filter(Boolean).join(' ').trim() || '-';
      const svcNames = (arr) => (arr || []).map((s) => s.service_name || s.test_name).filter(Boolean).join(', ') || '-';
      const rowTime = (row) => {
        if (row?.time) return String(row.time);
        if (!row?.createdAt) return '-';
        const dt = new Date(row.createdAt);
        if (isNaN(dt)) return '-';
        return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
      };

      // ── Column headers (matches PDF) ──────────────────────────────
      const COLS = ['Sr.No.', 'Date', 'Time', 'Type', 'Patient Name', 'UHID', 'Bill/Adm No.',
                    'Pay Mode', 'Ref By', 'Services', 'Panel',
                    'Credit Amt', 'Recd 1', 'Recd 2', 'Recd Amt'];

      const rows = [];

      rows.push(['OPD REFERRAL COLLECTION REPORT']);
      rows.push([`Period: ${formatDate(watch('from_date'))} to ${formatDate(watch('to_date'))}   |   Printed: ${formatDate(new Date().toISOString())}`]);
      rows.push([]);

      const pushSection = (title, data, mapFn, subtotalLabel, subtotalAmt) => {
        rows.push([title, '', '', '', '', '', '', '', '', '', '', '', '', '', `(${data.length} records)`]);
        rows.push(COLS);
        if (data.length === 0) {
          rows.push(['', '', '', '', 'No records found for this period.']);
        } else {
          data.forEach((row, i) => rows.push(mapFn(row, i)));
        }
        rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', subtotalLabel, rupees(subtotalAmt)]);
        rows.push([]);
      };

      // ── Section 1 — OPD Registration ──────────────────────────────
      pushSection(
        'OPD REGISTRATION (CONSULTANCY)',
        opdRows,
        (row, i) => [
          i + 1,
          formatDate(row.date),
          rowTime(row),
          'OPD',
          patName(row.name_suffix, row.first_name, row.last_name),
          row.uhid || '-',
          row.adm_no_date || '-',
          row.payment_mode || '-',
          row.refby?.referral_name || 'Direct / N.A.',
          'Consultancy',
          row.sponsor?.name || '-',
          row.payment_mode?.toUpperCase() === 'CREDIT' ? rupees(row.bal) : '-',
          '-',
          '-',
          rupees(row.recd)
        ],
        'OPD REGISTRATION TOTAL',
        opdStats.recd_amt
      );

      // ── Section 2 — OPD Billing ────────────────────────────────────
      pushSection(
        'OPD BILLING',
        opdBillRows,
        (row, i) => [
          i + 1,
          formatDate(row.date),
          rowTime(row),
          'OPD Bill',
          patName(row.patient?.name_suffix, row.patient?.first_name, row.patient?.last_name),
          row.uhid || '-',
          row.patient?.adm_no_date || '-',
          row.pay_mode || '-',
          row.patient?.ref_by?.referral_name || 'Direct / N.A.',
          svcNames(row.combinedServiceDetails),
          row.patient?.sponsor?.name || '-',
          row.pay_mode?.toUpperCase() === 'CREDIT' ? rupees(row.balance_amt) : '-',
          row.pay_mode_1 ? `${row.pay_mode_1} ${rupees(row.rec_amt_1)}` : (row.rec_amt_1 ? rupees(row.rec_amt_1) : '-'),
          row.pay_mode_2 ? `${row.pay_mode_2} ${rupees(row.rec_amt_2)}` : (row.rec_amt_2 ? rupees(row.rec_amt_2) : '-'),
          rupees(row.total_recd)
        ],
        'OPD BILLING TOTAL',
        opdBillStats.recd_amt
      );

      // ── Payment Mode Summary (same as PDF) ──────────────────────────
      const billingCash = n(opdStats.recd_amt_cash) + n(opdBillStats.recd_amt_cash);
      const billingBank = n(opdStats.recd_amt_bank) + n(opdBillStats.recd_amt_bank);
      const billingBoth = n(opdStats.recd_amt_both) + n(opdBillStats.recd_amt_both);
      const totalCredit = n(opdStats.recd_amt_credit) + n(opdBillStats.recd_amt_credit);
      const grandTotal  = n(opdStats.recd_amt) + n(opdBillStats.recd_amt);

      rows.push(['COLLECTION SUMMARY']);
      rows.push([]);
      rows.push(['PAYMENT MODE BREAKDOWN']);
      rows.push(['Cash', '', rupees(billingCash)]);
      rows.push(['Bank / Digital', '', rupees(billingBank)]);
      if (billingBoth > 0) {
        rows.push(['Both Cash+Bank', '', rupees(billingBoth)]);
      }
      if (totalCredit > 0) {
        rows.push(['Credit (Outstanding - Not Collected)', '', rupees(totalCredit)]);
      }
      rows.push(['NET COLLECTED', '', rupees(grandTotal)]);
      rows.push([]);

      // ── Collection by Referral (same as PDF) ────────────────────────
      rows.push(['COLLECTION BY REFERRAL', '', 'Patients', 'Amount']);
      referralSummary.forEach((r) => {
        rows.push([r.referral_name, '', r.opdCount + r.opdBillCount, rupees(r.totalAmt)]);
      });
      const referralGrandTotal = referralSummary.reduce((s, r) => s + n(r.totalAmt), 0);
      rows.push(['Total', '', '', rupees(referralGrandTotal)]);
      rows.push([]);

      rows.push(['GRAND TOTAL COLLECTED (OPD Registration + OPD Billing)', '', '', '', '', rupees(grandTotal)]);

      if (totalCredit > 0) {
        rows.push([]);
        rows.push([`* Credit Outstanding - on account, NOT collected (Cash/Bank flow excluded): ${rupees(totalCredit)}`]);
      }

      // ── Build sheet ────────────────────────────────────────────────
      const ws = XLSX.utils.aoa_to_sheet(rows);

      ws['!cols'] = [
        { wch: 5 },  // Sr
        { wch: 10 }, // Date
        { wch: 9 },  // Time
        { wch: 12 }, // Type
        { wch: 28 }, // Patient Name
        { wch: 10 }, // UHID
        { wch: 12 }, // Bill/Adm No
        { wch: 10 }, // Pay Mode
        { wch: 22 }, // Ref By
        { wch: 28 }, // Services
        { wch: 14 }, // Panel
        { wch: 12 }, // Credit Amt
        { wch: 16 }, // Recd 1
        { wch: 16 }, // Recd 2
        { wch: 12 }, // Recd Amt
      ];

      const wb = { Sheets: { 'OPD Referral Report': ws }, SheetNames: ['OPD Referral Report'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: fileType });
      saveAs(blob, `OPD_Referral_Report_${watch('from_date')}_to_${watch('to_date')}${fileExtension}`);

    } catch (err) {
      console.error('Excel export error:', err);
      toast('Failed to generate Excel report', { variant: 'error' });
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                OPD Referral Report
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={!watch('to_date')}
                  onClick={() => window.open(`/print${pathname}?refId=${buildRefId()}`, '_blank')}
                >
                  View Pdf Report
                </Button>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={!watch('to_date')}
                  onClick={handleExcelReport}
                >
                  Download Excel Report
                </Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '60%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={11} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Ref By</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="refby"
                      menus={[
                        { name: 'All', _id: 'all' },
                        ...referralDropdown.map((data) => ({ ...data, name: data.referral_name }))
                      ]}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>From Date</Typography>
                    <TextField sx={{ flex: 0.3 }} name="from_date" type="date" />
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField sx={{ flex: 0.3 }} name="to_date" type="date" />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};

export default OpdReferralReport;
